/**
 * "Bexio-Rechnungen (Vorschau)" — the newest few mirrored invoices.
 *
 * Fetched with an explicit small limit, not sliced from a full load. The old
 * overview paged through every invoice (up to 40 requests) to render a table
 * nobody reads on a dashboard; five rows need one request.
 *
 * Order is the server's — `issue_date DESC, bexio_invoice_id DESC` — so "newest"
 * means what the backend means by it, and no second sort can disagree.
 *
 * COLUMN NAMING: the design reference labels the second column "Kontakt" but
 * shows invoice titles ("Miete Oktober 2026") under it. Both fields exist and
 * they are different things, so the column is called "Titel" and the contact
 * rides underneath it. A header that names the wrong field is worse than a
 * layout that differs from the mockup.
 *
 * COLUMN COUNT: the reference shows five columns (adding "Datum"). Measured in
 * the browser at the real card width of 438px, the five come out at 88px each
 * while "CHF 1'400.00" alone needs 110px — the amount clipped and the title fell
 * to 64px. A dashboard preview that truncates money is worse than one without a
 * date column, and these five rows are already ordered newest-first, so the date
 * is the column that goes. It returns on the invoices page.
 *
 * No footer link: there is no invoices route yet. The total is shown as text.
 */
import React from "react";

import { INVOICE_STATUS_LABEL, fmtInvoiceMoney, mapLabel } from "../bexioFormat";
import BexioPreviewFooter from "./BexioPreviewFooter";

/** Status pill tones. Written literally per status so the JIT keeps them. */
const STATUS_TONE = {
  draft:
    "border-border bg-muted text-muted-foreground",
  pending:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  partial:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  paid:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  canceled:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  cancelled:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
};

export default function BexioInvoicesPreview({
  invoices,
  total,
  onLoadMore,
  loadingMore,
}) {
  const rows = Array.isArray(invoices) ? invoices : [];

  return (
    <section
      aria-labelledby="bexio-invoices-preview-title"
      className="flex flex-col rounded-xl border border-border bg-card"
    >
      <div className="border-b border-border px-4 py-3">
        <h2
          id="bexio-invoices-preview-title"
          className="text-sm font-semibold text-foreground"
        >
          Bexio-Rechnungen (Vorschau)
        </h2>
      </div>

      {rows.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">
          Keine Rechnungen vorhanden.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-left">
                <th scope="col" className="w-[21%] whitespace-nowrap px-2 py-2 text-xs font-medium text-muted-foreground">
                  Beleg-Nr.
                </th>
                <th scope="col" className="w-[33%] px-2 py-2 text-xs font-medium text-muted-foreground">
                  Titel
                </th>
                <th scope="col" className="w-[21%] px-2 py-2 text-xs font-medium text-muted-foreground">
                  Status
                </th>
                <th scope="col" className="whitespace-nowrap px-2 py-2 text-right text-xs font-medium text-muted-foreground">
                  Betrag
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id ?? r.bexio_invoice_id} className="border-b border-border last:border-0">
                  <td className="whitespace-nowrap px-2 py-2 font-medium text-foreground">
                    {r.document_nr || "—"}
                  </td>
                  <td className="px-2 py-2">
                    <div className="truncate text-foreground" title={r.title || undefined}>
                      {r.title || "—"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-2 py-2">
                    <span
                      className={`inline-flex whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[11px] font-medium ${
                        STATUS_TONE[r.bexio_status] || "border-border bg-muted text-muted-foreground"
                      }`}
                    >
                      {mapLabel(INVOICE_STATUS_LABEL, r.bexio_status)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-foreground">
                    {fmtInvoiceMoney(r.total, r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BexioPreviewFooter
        shown={rows.length}
        total={total}
        noun="Rechnung"
        nounPlural="Rechnungen"
        onLoadMore={onLoadMore}
        loadingMore={loadingMore}
      />
    </section>
  );
}
